import type { ButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { formatDate } from '@/shared/lib';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Button, DataRecordCard } from '@/shared/ui';
import { deleteIntegration, selectIsLoading } from '../../model/slice';
import type {
  Datasource,
  TikTokForBusinessReportingIntegration,
} from '../../model/types';
import { useDatasourceIntegrations } from '../../model/useDatasourceIntegrations';
import { useURLSearchParamsBroadcaster } from '../../model/useURLSearchParamsBroadcaster';
import { DatasourcePanel } from '../DatasourcePanel/DatasourcePanel';
import { IntegrationSyncInfo } from '../IntegrationSyncInfo/IntegrationSyncInfo';
import { BROADCAST_CHANNEL_NAME } from './model/constants';
import { useTikTokAuthCode } from './model/useTikTokAuthCode';
import { TikTokIcon } from './ui/TikTokIcon/TikTokIcon';

const TikTokButton = styled((props: ButtonProps) => {
  return (
    <Button
      variant="outlined"
      color="secondary"
      startIcon={<TikTokIcon />}
      {...props}
    />
  );
})(({ theme }) => ({
  color: '#161823',
  borderRadius: theme.shape.borderRadius * 2,
  height: 40,
  fontSize: '0.75rem',
  lineHeight: '1.125rem',
})) as typeof Button;

interface Props {
  datasource: Datasource;
}

export function TikTokForBusinessDatasource({ datasource }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const integrationsLoading = useAppSelector(selectIsLoading);
  const tikTokForBusinessReportingIntegrations = useDatasourceIntegrations(
    datasource,
  ) as TikTokForBusinessReportingIntegration[];
  const { processing, openDataAccessDialog } = useTikTokAuthCode();

  useURLSearchParamsBroadcaster({
    channelName: BROADCAST_CHANNEL_NAME,
    params: ['code', 'auth_code'],
  });

  const handleDeleteIntegration = (id: string) => () => {
    return dispatch(deleteIntegration(id))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Integration successfully deleted.', {
          variant: 'success',
          autoHideDuration: 2000,
        });
      })
      .catch(() => {
        enqueueSnackbar('Unable to delete integration. Please, try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      });
  };

  return (
    <DatasourcePanel
      loading={integrationsLoading || processing}
      datasource={datasource}
      addButtonSlot={
        <TikTokButton onClick={openDataAccessDialog}>Add account</TikTokButton>
      }
      integrationsSlot={tikTokForBusinessReportingIntegrations.map(
        (integration) => {
          return (
            <DataRecordCard
              key={integration.id}
              recordNoun="integration"
              title={integration.integrationConfig.email}
              titleExtra={
                <IntegrationSyncInfo
                  status={integration.status}
                  lastSuccessfulSync={integration.lastSuccessfulSync}
                />
              }
              fields={[
                {
                  label: 'Last update date',
                  value: formatDate(integration.updatedAt),
                },
              ]}
              onDelete={handleDeleteIntegration(integration.id)}
            />
          );
        },
      )}
    />
  );
}
